import { HttpClient } from '@angular/common/http';
import { EnvironmentProviders, inject, Injectable, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { APP_BASE_CONFIG, APP_CONFIG } from './app-config.tokens';
import type { AppConfig } from './app-config.types';

const DEFAULT_RUNTIME_CONFIG: Partial<AppConfig> = {
  maintenance: false,
  internalGate: true,
  featureFlags: {
    buyLandTitles: true,
  },
};

@Injectable({ providedIn: 'root' })
export class AppConfigInitializer {
  private readonly http = inject(HttpClient);
  private readonly baseConfig = inject(APP_BASE_CONFIG);
  private readonly config: AppConfig = this.mergeConfig(DEFAULT_RUNTIME_CONFIG);

  get value(): AppConfig {
    return this.config;
  }

  async load(): Promise<void> {
    await this.http
      .get<Partial<AppConfig>>('/app-config.json')
      .pipe(
        catchError(() => of(DEFAULT_RUNTIME_CONFIG)),
        map((runtimeConfig) => {
          Object.assign(this.config, this.mergeConfig(runtimeConfig));
        })
      )
      .toPromise();
  }

  private mergeConfig(runtimeConfig?: Partial<AppConfig>): AppConfig {
    const runtime = {
      ...DEFAULT_RUNTIME_CONFIG,
      ...runtimeConfig,
      featureFlags: {
        ...DEFAULT_RUNTIME_CONFIG.featureFlags,
        ...(runtimeConfig?.featureFlags ?? {}),
      },
    };

    return {
      ...this.baseConfig,
      ...runtime,
      featureFlags: {
        ...(this.baseConfig?.featureFlags ?? {}),
        ...runtime.featureFlags,
      },
    } as AppConfig;
  }
}

export function appConfigInitializerFactory(configInitializer: AppConfigInitializer): () => Promise<void> {
  return () => configInitializer.load();
}

export function appConfigFactory(configInitializer: AppConfigInitializer): AppConfig {
  return configInitializer.value;
}

export function provideAppConfig(baseConfig: Partial<AppConfig>): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: APP_BASE_CONFIG, useValue: baseConfig as AppConfig },
    provideAppInitializer(() => inject(AppConfigInitializer).load()),
    {
      provide: APP_CONFIG,
      useFactory: appConfigFactory,
      deps: [AppConfigInitializer],
    },
  ]);
}
